import { useEffect, useRef, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import type { EChartsCoreOption, EChartsType } from "echarts/core";

export const gameIdFromChartEvent = (event: unknown): string | null => {
  if (!event || typeof event !== "object") return null;
  const { seriesName, data } = event as { seriesName?: unknown; data?: unknown };
  if (seriesName !== "Observed" || !data || typeof data !== "object") return null;
  const gameId = (data as { gameId?: unknown }).gameId;
  return typeof gameId === "string" && gameId.length > 0 ? gameId : null;
};

export const ChesslaChart = ({
  option,
  label,
  height = 300,
  kind,
  onGameSelect,
}: {
  option: EChartsCoreOption;
  label: string;
  height?: number | string;
  kind: string;
  onGameSelect?: (gameId: string) => void;
}) => {
  const root = useRef<HTMLDivElement | null>(null);
  const chart = useRef<EChartsType | null>(null);
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const latestOption = useRef(option);
  const latestReducedMotion = useRef(reducedMotion);
  const latestOnGameSelect = useRef(onGameSelect);
  const [visible, setVisible] = useState(false);
  const [failed, setFailed] = useState(false);
  latestOption.current = option;
  latestReducedMotion.current = reducedMotion;
  latestOnGameSelect.current = onGameSelect;

  useEffect(() => {
    const element = root.current;
    if (!element) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "80px 0px", threshold: 0.08 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!visible) return;
    if (!root.current) return;
    const element = root.current;
    let disposed = false,
      observer: ResizeObserver | undefined,
      instance: EChartsType | undefined;
    const resize = () => instance?.resize();
    void import("./echarts-runtime")
      .then(({ init }) => {
        if (disposed) return;
        instance = init(element, undefined, { renderer: "svg" });
        chart.current = instance;
        instance.setOption(
          {
            ...latestOption.current,
            animation: latestOption.current.animation !== false && !latestReducedMotion.current,
          },
          { notMerge: true },
        );
        instance.on("click", (event) => {
          const gameId = gameIdFromChartEvent(event);
          if (gameId) latestOnGameSelect.current?.(gameId);
        });
        if (typeof ResizeObserver === "undefined") window.addEventListener("resize", resize);
        else {
          observer = new ResizeObserver(resize);
          observer.observe(element);
        }
      })
      .catch(() => {
        if (!disposed) setFailed(true);
      });
    return () => {
      disposed = true;
      observer?.disconnect();
      window.removeEventListener("resize", resize);
      instance?.dispose();
      chart.current = null;
    };
  }, [visible]);
  useEffect(() => {
    chart.current?.setOption(
      { ...option, animation: option.animation !== false && !reducedMotion },
      { notMerge: true },
    );
  }, [option, reducedMotion, visible]);
  return (
    <Box
      ref={root}
      role="img"
      aria-label={label}
      data-chart-kind={kind}
      sx={{ width: "100%", minWidth: 0, height, overflow: "hidden", touchAction: "pan-y" }}
    >
      {failed && (
        <Typography sx={{ p: 2, color: "text.secondary", fontSize: 13 }}>
          Chart unavailable. Exact data remains below.
        </Typography>
      )}
    </Box>
  );
};
